import { useCallback, useEffect, useRef } from 'react'
import { fetchCruiseOffer } from 'actions/CruiseActions'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'

interface UseCruiseOfferDetailArgs {
  offerId: string | undefined
  enabled?: boolean
}

export default function useCruiseOfferDetail({
  offerId,
  enabled = true,
}: UseCruiseOfferDetailArgs) {
  const dispatch = useAppDispatch()
  const attemptedOfferIdRef = useRef<string | undefined>(undefined)

  const offer = useAppSelector((state) => offerId ? state.cruise.cruiseOffers[offerId] : undefined)
  const isLoading = useAppSelector((state) => offerId ? !!state.cruise.cruiseOffersLoading[offerId] : false)
  const error = useAppSelector((state) => offerId ? state.cruise.cruiseOffersErrors[offerId] : undefined)

  const fetchOffer = useCallback(() => {
    if (!offerId || !enabled) return
    attemptedOfferIdRef.current = offerId
    dispatch(fetchCruiseOffer(offerId))
  }, [dispatch, enabled, offerId])

  useEffect(() => {
    if (!enabled || !offerId) {
      attemptedOfferIdRef.current = undefined
      return
    }

    if (offer || isLoading || (error && attemptedOfferIdRef.current === offerId)) return
    fetchOffer()
  }, [enabled, error, fetchOffer, isLoading, offer, offerId])

  return {
    offer,
    // Treat the pre-dispatch first-load frame as loading so the drawer never flashes empty content.
    isLoading: enabled && !!offerId && (isLoading || (!offer && !error)),
    isError: !!error,
    refetch: fetchOffer,
  }
}
